import styled from "styled-components";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { postAnalytics } from "../../../../PatientComponents/analytics";
import { buttonCSS, desktopCss } from "./general.style";
import doctor2Img from "../../../assets/Mayo/Doctor4.jpg";
import Transition from "./Transition";
import { usePatientFacing } from "../../../flow/patientFacing/hooks/usePatientFacing";
import GIPatientHeader from "./components/GIPatientHeader";
import TermsModal from "./components/TermsModal";

export default function GILanding() {
  const { t } = useTranslation();
  const { getEventDataLocal } = usePatientFacing();
  const event = getEventDataLocal();
  const caseId = event?.Case?.id;
  const [showTerms, setShowTerms] = useState(false);
  const navigate = useNavigate();

  // Helper to get progress for 'gi' facing type
  const getGiProgress = () => {
    const cpData = event?.Case?.CasesProgresses;
    const casesProgressArray = Array.isArray(cpData) ? cpData : (cpData ? [cpData] : []);
    return casesProgressArray.find(cp => cp?.facingType === 'gi');
  };

  const giProgress = getGiProgress();
  const hasAvatar = !!giProgress?.avatarSelection;
  const hasQuestionnaire = !!giProgress?.answeredQuestionnaire;

  // Synchronous redirect based on progress (runs before paint)
  // If questionnaire is done, avatar was definitely chosen — go to video
  if (event && hasQuestionnaire) {
    return <Navigate to="../gi/video" replace />;
  }
  // If only avatar is done (questionnaire not yet), skip to questionnaire
  if (event && hasAvatar && !hasQuestionnaire) {
    return <Navigate to="../gi/Questionnaire/diabetesMedicines" replace />;
  }

  // Determine where the Start button should lead
  const getStartButtonDestination = () => {
    return "../gi/choose-avatar";
  };

  // Handle missing event data (expired or invalid link)
  if (!event) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: '20px',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        textAlign: 'center',
        background: '#f5f5f5'
      }}>
        <div style={{
          background: '#f8f9fa',
          borderRadius: '12px',
          padding: '40px',
          maxWidth: '500px',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>⏰</div>
          <h1 style={{ color: '#dc3545', marginBottom: '16px', fontSize: '24px' }}>Link Expired</h1>
          <p style={{ color: '#6c757d', fontSize: '16px', lineHeight: '1.5' }}>
            This link has expired or is no longer valid.
          </p>
          <p style={{ color: '#6c757d', fontSize: '14px', marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #dee2e6' }}>
            If you have questions about your procedure, please contact your healthcare provider.
          </p>
        </div>
      </div>
    );
  }

  const handleLegalLinkClick = () => {
    postAnalytics({ userId: caseId, type: "opened-tos" });
    setShowTerms(true);
  };

  const handleStartClick = () => {
    postAnalytics({ userId: caseId, type: "start-button-clicked" });
  };

  return (
    <Transition>
      <StartContainer>
        <GIPatientHeader />
        <DoctorImage id="startPageNurse" src={doctor2Img} alt="doctor" />
        <ParagraphWrapper id="ParagraphWrapper">
          <Title id="HelloTitle">
            {t("Start-Title")}
          </Title>
          <Paragraph id="StartParagraph">
            {t("Start-Paragraph")}
          </Paragraph>
        </ParagraphWrapper>
        <BottomContentContainer>
          <StartButton
            id="StartButton"
            to={getStartButtonDestination()}
            onClick={handleStartClick}
          >
            {t("Start-Button")}
          </StartButton>
          <LegalTextWrapper>
            {t("Start-Legal-Explain")}
            &nbsp;
            <LegalLink
              id="LegalLink"
              onClick={handleLegalLinkClick}
            >
              {t("Start-Legal-Link")}
            </LegalLink>
          </LegalTextWrapper>
        </BottomContentContainer>
      </StartContainer>
      <TermsModal isOpen={showTerms} onClose={() => setShowTerms(false)} />
    </Transition>
  );
}

const StartContainer = styled.div`
  ${desktopCss}
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-inline: 22px;
  padding-block-end: 1.2rem;
  padding-block-start: 4px;
  box-sizing: border-box;
  justify-content: space-between;
`;

const DoctorImage = styled.img`
  width: 280px;
  max-width: 100%;
  object-fit: contain;
`;

const Title = styled.h1`
  font-family: "Abraham", sans-serif;
  font-weight: 700;
  font-size: 1.5rem;
  margin: 0;
  text-align: center;
`;

const BottomContentContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
`;

const Paragraph = styled.p`
  font-family: "Abraham", sans-serif;
  font-weight: 400;
  font-size: 1.1875rem;
  margin-block: 0;
  text-align: center;
  line-height: 1.5;
`;

const StartButton = styled(Link)`
  ${buttonCSS}
  width: 175px;
  height: 43px;
  border-radius: 29px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0px 3px 6px rgba(0, 0, 0, 0.29);
  margin-top: 20px;
`;

const LegalLink = styled.button`
  background: none;
  border: none;
  color: #7A9DFD;
  cursor: pointer;
  font-family: "Abraham", sans-serif;
  font-weight: bold;
  font-size: 1rem;
  padding: 0;
  
  &:hover {
    text-decoration: underline;
  }
`;
const LegalTextWrapper = styled.p`
  font-family: "Abraham", sans-serif;
  font-weight: 400;
  font-size: 1rem;
  margin-block: 0;
  margin-top: 10px;
`;
const ParagraphWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.4rem;
  width: 100%;
  padding-inline: 10px;
  box-sizing: border-box;
  text-align: center;
  margin-top: 1.5rem;
`;
